import { copy } from '@ohear/shared'
import { Spinner } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
/**
 * Actor batch-upload route — deliver a whole recording batch as files.
 *
 * Mirrors the record route's shell: resolve the actor's project, render the
 * sticky sub-nav (Script review / Record / Upload tabs), then mount the
 * lazy-loaded BatchUploadPage with the project's batches. The page itself
 * owns batch selection (defaulting to the in_progress batch), the dropzone,
 * and the file→cue mapping table.
 */
import { Suspense, lazy } from 'react'
import { useActorProject } from '../../../lib/api/projects'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'

const BatchUploadPage = lazy(() =>
  import('../../../features/upload-batch/BatchUploadPage').then((m) => ({
    default: m.BatchUploadPage,
  })),
)

export const Route = createFileRoute('/actor/projects/$projectId/upload')({
  component: ActorBatchUploadPage,
})

function ActorBatchUploadPage() {
  useRequireRole(['actor'])
  const t = useT()
  const { projectId } = Route.useParams()
  const project = useActorProject(projectId)

  if (project.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-pt-bg">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-pt-text-soft">
          Loading project…
        </p>
      </div>
    )
  }
  if (project.isError) {
    const msg = project.error instanceof Error ? project.error.message : 'unknown'
    return (
      <div className="bg-pt-bg p-6">
        <p className="mb-2 inline-block rounded-sm border border-pt-text bg-pt-warn px-3 py-1.5 text-pt-text">
          Could not load project: {msg}
        </p>
        <div className="mt-3">
          <Link to="/actor" className="text-pt-wave hover:underline">
            Back to projects
          </Link>
        </div>
      </div>
    )
  }
  const data = project.data
  if (!data) return null

  const batches = data.batches ?? []
  if (batches.length === 0) {
    return (
      <div className="bg-pt-bg p-6">
        <p className="text-pt-text">{t(copy.common.EMPTY.no_batches_assigned)}</p>
        <Link to="/actor" className="text-pt-wave hover:underline">
          Back to projects
        </Link>
      </div>
    )
  }

  return (
    <section style={{ minHeight: 'calc(100vh - 80px)' }}>
      {/* Same PT-style sticky sub-nav as the record route, Upload tab armed. */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-pt-text bg-pt-chrome px-4 py-1.5 text-[12px] shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(0,0,0,0.18)]">
        <div className="flex items-center gap-2 font-mono uppercase tracking-[0.12em]">
          <Link
            to="/actor"
            className="text-pt-text hover:text-pt-wave transition-colors"
            aria-label="Back to projects"
          >
            ← Projects
          </Link>
          <span className="text-pt-text-soft">|</span>
          <span className="text-pt-text-soft">{data.client_name}</span>
          <span className="text-pt-text-soft">|</span>
          <span className="flex items-center gap-2 normal-case tracking-normal">
            {data.characters.map((c, i) => (
              <span key={c.character_id} className="flex items-center gap-2">
                {i > 0 ? <span className="text-pt-text-soft">/</span> : null}
                <span className="text-pt-wave font-bold">[{c.display_name}]</span>
                <span className="font-mono text-pt-text-soft">{c.character_code}</span>
              </span>
            ))}
          </span>
        </div>
        {/* PT segmented tab control (script-review / record / upload). */}
        <div className="inline-flex items-stretch overflow-hidden rounded-sm" role="group">
          <Link
            to="/actor/projects/$projectId/script-review"
            params={{ projectId }}
            className="pt-btn flex items-center px-3 py-1 text-[11px] rounded-l-sm rounded-r-none -mr-px"
          >
            Script review
          </Link>
          <Link
            to="/actor/projects/$projectId/record"
            params={{ projectId }}
            className="pt-btn flex items-center px-3 py-1 text-[11px] rounded-none -mr-px"
          >
            Record
          </Link>
          <Link
            to="/actor/projects/$projectId/upload"
            params={{ projectId }}
            aria-pressed="true"
            className="pt-btn pt-btn--armed flex items-center px-3 py-1 text-[11px] rounded-r-sm rounded-l-none"
            style={{
              background: 'linear-gradient(180deg, #ffe97a 0%, #ffd54f 100%)',
              boxShadow:
                'inset 0 2px 3px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(0,0,0,0.18), 0 0 8px rgba(255,213,79,0.65)',
            }}
          >
            {t(copy.upload.BATCH.TAB)}
          </Link>
        </div>
      </div>

      <Suspense
        fallback={
          <div className="flex justify-center p-12">
            <Spinner size="lg" label="Loading upload UI…" />
          </div>
        }
      >
        <BatchUploadPage projectId={projectId} batches={batches} />
      </Suspense>
    </section>
  )
}
